import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getAppIdFromStorage,
  projectNameToAppId,
} from "../../../utils/appIdStorage";
import {
  getClientNotificationCenterBilling,
  getClientNotificationCenterConsumption,
  getClientNotificationCenterCounters,
  getClientNotificationCenterInvoices,
  getClientNotificationCenterMessages,
  getClientNotificationCenterProduct,
  getClientNotificationCenterRegistrationTracker,
  getClientNotificationCenterReporting,
  getClientNotificationCenterReportingCalendar,
  getClientNotificationCenterSettings,
  getClientNotificationCenterThresholds,
  getClientNotificationCenterTransactions,
  markClientNotificationCenterSeen,
} from "./clientNotificationCenterApi";
import {
  CLIENT_NOTIFICATION_CENTER_BILLING_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_CONSUMPTION_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_INVOICES_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_MESSAGES_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
  CLIENT_NOTIFICATION_CENTER_PRODUCT_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_REGISTRATION_TRACKER_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_REPORTING_CALENDAR_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_REPORTING_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_SETTINGS_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_THRESHOLDS_TABLE_ID,
  CLIENT_NOTIFICATION_CENTER_TRANSACTIONS_TABLE_ID,
} from "./clientNotificationCenterConstants";

function parsePositiveInt(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * `openProjectName` follows the project switcher, while `app_Id` lags behind it
 * until GET app resolves, so the selected project wins.
 */
export function resolveClientNotificationCenterAppId(state, explicitAppId) {
  return (
    parsePositiveInt(explicitAppId) ??
    parsePositiveInt(projectNameToAppId(state?.user?.openProjectName)) ??
    parsePositiveInt(state?.user?.app_Id) ??
    parsePositiveInt(getAppIdFromStorage()) ??
    1
  );
}

function unwrapObject(raw) {
  return raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
    ? raw.data
    : raw;
}

export function normalizeClientNotificationCenterCounters(raw) {
  const source = unwrapObject(raw);
  const incoming = source?.by_table ?? source?.byTable ?? {};

  return {
    total: Number(source?.total) || 0,
    byTable: {
      [CLIENT_NOTIFICATION_CENTER_REPORTING_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_REPORTING_TABLE_ID]) || 0,
      [CLIENT_NOTIFICATION_CENTER_REPORTING_CALENDAR_TABLE_ID]:
        Number(
          incoming[CLIENT_NOTIFICATION_CENTER_REPORTING_CALENDAR_TABLE_ID]
        ) || 0,
      [CLIENT_NOTIFICATION_CENTER_MESSAGES_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_MESSAGES_TABLE_ID]) || 0,
      [CLIENT_NOTIFICATION_CENTER_REGISTRATION_TRACKER_TABLE_ID]:
        Number(
          incoming[CLIENT_NOTIFICATION_CENTER_REGISTRATION_TRACKER_TABLE_ID]
        ) || 0,
      [CLIENT_NOTIFICATION_CENTER_THRESHOLDS_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_THRESHOLDS_TABLE_ID]) || 0,
      [CLIENT_NOTIFICATION_CENTER_PRODUCT_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_PRODUCT_TABLE_ID]) || 0,
      [CLIENT_NOTIFICATION_CENTER_BILLING_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_BILLING_TABLE_ID]) || 0,
      [CLIENT_NOTIFICATION_CENTER_CONSUMPTION_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_CONSUMPTION_TABLE_ID]) || 0,
      [CLIENT_NOTIFICATION_CENTER_SETTINGS_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_SETTINGS_TABLE_ID]) || 0,
      [CLIENT_NOTIFICATION_CENTER_INVOICES_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_INVOICES_TABLE_ID]) || 0,
      [CLIENT_NOTIFICATION_CENTER_TRANSACTIONS_TABLE_ID]:
        Number(incoming[CLIENT_NOTIFICATION_CENTER_TRANSACTIONS_TABLE_ID]) || 0,
    },
  };
}

export function normalizeClientNotificationCenterTable(
  raw,
  tableId,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  const source = unwrapObject(raw);
  const parsedOffset = Number(source?.offset);
  const parsedLimit = Number(source?.limit);

  return {
    tableId: source?.table_id ?? tableId,
    items: Array.isArray(source?.items) ? source.items : [],
    total: Number(source?.total) || 0,
    offset: Number.isFinite(parsedOffset) ? parsedOffset : fallbackOffset,
    limit: Number.isFinite(parsedLimit) ? parsedLimit : fallbackLimit,
  };
}

export function normalizeClientNotificationCenterReporting(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_REPORTING_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterReportingCalendar(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_REPORTING_CALENDAR_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterMessages(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_MESSAGES_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterRegistrationTracker(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_REGISTRATION_TRACKER_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterThresholds(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_THRESHOLDS_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterProduct(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_PRODUCT_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterBilling(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_BILLING_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterSettings(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_SETTINGS_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterInvoices(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_INVOICES_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterTransactions(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_TRANSACTIONS_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

export function normalizeClientNotificationCenterConsumption(
  raw,
  fallbackOffset = 0,
  fallbackLimit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
) {
  return normalizeClientNotificationCenterTable(
    raw,
    CLIENT_NOTIFICATION_CENTER_CONSUMPTION_TABLE_ID,
    fallbackOffset,
    fallbackLimit
  );
}

function resolveClientNotificationCenterTableId(tableId) {
  if (tableId === CLIENT_NOTIFICATION_CENTER_MESSAGES_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_MESSAGES_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_REGISTRATION_TRACKER_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_REGISTRATION_TRACKER_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_THRESHOLDS_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_THRESHOLDS_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_PRODUCT_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_PRODUCT_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_BILLING_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_BILLING_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_SETTINGS_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_SETTINGS_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_INVOICES_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_INVOICES_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_REPORTING_CALENDAR_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_REPORTING_CALENDAR_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_TRANSACTIONS_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_TRANSACTIONS_TABLE_ID;
  }
  if (tableId === CLIENT_NOTIFICATION_CENTER_CONSUMPTION_TABLE_ID) {
    return CLIENT_NOTIFICATION_CENTER_CONSUMPTION_TABLE_ID;
  }
  return CLIENT_NOTIFICATION_CENTER_REPORTING_TABLE_ID;
}

export const fetchClientNotificationCenterCounters = createAsyncThunk(
  "clientNotificationCenter/fetchCounters",
  async ({ appId, silent = false } = {}, { getState, rejectWithValue }) => {
    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterCounters(resolvedAppId);
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterCounters(await response.json()),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterReporting = createAsyncThunk(
  "clientNotificationCenter/fetchReporting",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterReporting({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterReporting(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterReportingCalendar = createAsyncThunk(
  "clientNotificationCenter/fetchReportingCalendar",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterReportingCalendar({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterReportingCalendar(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterMessages = createAsyncThunk(
  "clientNotificationCenter/fetchMessages",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterMessages({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterMessages(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterRegistrationTracker = createAsyncThunk(
  "clientNotificationCenter/fetchRegistrationTracker",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterRegistrationTracker({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterRegistrationTracker(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterThresholds = createAsyncThunk(
  "clientNotificationCenter/fetchThresholds",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterThresholds({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterThresholds(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterProduct = createAsyncThunk(
  "clientNotificationCenter/fetchProduct",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterProduct({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterProduct(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterBilling = createAsyncThunk(
  "clientNotificationCenter/fetchBilling",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterBilling({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterBilling(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterSettings = createAsyncThunk(
  "clientNotificationCenter/fetchSettings",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterSettings({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterSettings(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterInvoices = createAsyncThunk(
  "clientNotificationCenter/fetchInvoices",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterInvoices({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterInvoices(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterTransactions = createAsyncThunk(
  "clientNotificationCenter/fetchTransactions",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterTransactions({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterTransactions(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

export const fetchClientNotificationCenterConsumption = createAsyncThunk(
  "clientNotificationCenter/fetchConsumption",
  async (
    {
      appId,
      offset = 0,
      limit = CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
    } = {},
    { getState, rejectWithValue }
  ) => {
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(
      1,
      Number(limit) || CLIENT_NOTIFICATION_CENTER_PAGE_SIZE
    );

    try {
      const resolvedAppId = resolveClientNotificationCenterAppId(getState(), appId);
      const response = await getClientNotificationCenterConsumption({
        appId: resolvedAppId,
        offset: safeOffset,
        limit: safeLimit,
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      return {
        ...normalizeClientNotificationCenterConsumption(
          await response.json(),
          safeOffset,
          safeLimit
        ),
        appId: resolvedAppId,
        silent,
      };
    } catch (error) {
      return rejectWithValue(
        error?.message || "Error"
      );
    }
  }
);

/**
 * POST /notify/client_notification_center/{id}/seen
 * Logs server response; then silently refreshes counters + source table.
 */
export const markClientNotificationCenterAsSeen = createAsyncThunk(
  "clientNotificationCenter/markAsSeen",
  async (
    { id, tableId, offset, limit } = {},
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const notificationId = parsePositiveInt(id);
      if (!notificationId) {
        throw new Error("Notification id is required");
      }

      const response = await markClientNotificationCenterSeen(notificationId);
      const responseText = await response.text();
      let responseBody = null;
      try {
        responseBody = responseText ? JSON.parse(responseText) : null;
      } catch {
        responseBody = responseText;
      }

      console.log("[client_notification_center] POST seen response", {
        id: notificationId,
        ok: response.ok,
        status: response.status,
        body: responseBody,
      });

      if (!response.ok) {
        const detail =
          responseBody && typeof responseBody === "object"
            ? responseBody.detail
            : null;
        throw new Error(
          response.errorMessage ||
            (typeof detail === "string" && detail.trim() !== ""
              ? detail
              : "Error")
        );
      }

      const state = getState().clientNotificationCenter;
      const appId = resolveClientNotificationCenterAppId(getState());
      const sourceTableId = resolveClientNotificationCenterTableId(tableId);
      const sourceTableState = state?.[sourceTableId];
      const fetchSourceTableById = {
        [CLIENT_NOTIFICATION_CENTER_MESSAGES_TABLE_ID]:
          fetchClientNotificationCenterMessages,
        [CLIENT_NOTIFICATION_CENTER_REGISTRATION_TRACKER_TABLE_ID]:
          fetchClientNotificationCenterRegistrationTracker,
        [CLIENT_NOTIFICATION_CENTER_THRESHOLDS_TABLE_ID]:
          fetchClientNotificationCenterThresholds,
        [CLIENT_NOTIFICATION_CENTER_PRODUCT_TABLE_ID]:
          fetchClientNotificationCenterProduct,
        [CLIENT_NOTIFICATION_CENTER_BILLING_TABLE_ID]:
          fetchClientNotificationCenterBilling,
        [CLIENT_NOTIFICATION_CENTER_SETTINGS_TABLE_ID]:
          fetchClientNotificationCenterSettings,
        [CLIENT_NOTIFICATION_CENTER_INVOICES_TABLE_ID]:
          fetchClientNotificationCenterInvoices,
        [CLIENT_NOTIFICATION_CENTER_TRANSACTIONS_TABLE_ID]:
          fetchClientNotificationCenterTransactions,
        [CLIENT_NOTIFICATION_CENTER_CONSUMPTION_TABLE_ID]:
          fetchClientNotificationCenterConsumption,
        [CLIENT_NOTIFICATION_CENTER_REPORTING_CALENDAR_TABLE_ID]:
          fetchClientNotificationCenterReportingCalendar,
        [CLIENT_NOTIFICATION_CENTER_REPORTING_TABLE_ID]:
          fetchClientNotificationCenterReporting,
      };
      const fetchSourceTable = fetchSourceTableById[sourceTableId];

      await Promise.all([
        dispatch(
          fetchClientNotificationCenterCounters({ appId, silent: true })
        ).unwrap(),
        dispatch(
          fetchSourceTable({
            appId,
            offset: offset ?? sourceTableState?.offset ?? 0,
            limit:
              limit ??
              sourceTableState?.limit ??
              CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
            silent: true,
          })
        ).unwrap(),
      ]);

      return { id: notificationId, tableId: sourceTableId, body: responseBody };
    } catch (error) {
      return rejectWithValue({
        tableId,
        message: error?.message || "Error",
      });
    }
  }
);

function createInitialState() {
  const createTableState = () => ({
    loading: false,
    error: null,
    items: [],
    total: 0,
    offset: 0,
    limit: CLIENT_NOTIFICATION_CENTER_PAGE_SIZE,
  });

  return {
    reporting: createTableState(),
    reporting_calendar: createTableState(),
    messages: createTableState(),
    registration_tracker: createTableState(),
    thresholds: createTableState(),
    product: createTableState(),
    billing: createTableState(),
    settings: createTableState(),
    invoices: createTableState(),
    transactions: createTableState(),
    consumption: createTableState(),
    counters: normalizeClientNotificationCenterCounters(null),
    countersLoading: false,
    countersError: null,
    countersHydrated: false,
    appId: null,
  };
}

const clientNotificationCenterSlice = createSlice({
  name: "clientNotificationCenter",
  initialState: createInitialState(),
  reducers: {
    clearClientNotificationCenterError: (state) => {
      state.reporting.error = null;
      state.reporting_calendar.error = null;
      state.messages.error = null;
      state.registration_tracker.error = null;
      state.thresholds.error = null;
      state.product.error = null;
      state.billing.error = null;
      state.settings.error = null;
      state.invoices.error = null;
      state.transactions.error = null;
      state.consumption.error = null;
      state.countersError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientNotificationCenterCounters.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.countersLoading = true;
        }
        state.countersError = null;
      })
      .addCase(fetchClientNotificationCenterCounters.fulfilled, (state, action) => {
        state.countersLoading = false;
        state.counters = {
          total: action.payload.total,
          byTable: action.payload.byTable,
        };
        state.countersHydrated = true;
        state.appId = action.payload.appId;
      })
      .addCase(fetchClientNotificationCenterCounters.rejected, (state, action) => {
        state.countersLoading = false;
        state.countersError =
          action.payload || action.error?.message || "Error";
      })
      .addCase(fetchClientNotificationCenterReporting.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.reporting.loading = true;
        }
        state.reporting.error = null;
      })
      .addCase(fetchClientNotificationCenterReporting.fulfilled, (state, action) => {
        const { items, total, offset, limit, appId } = action.payload;
        state.reporting = {
          loading: false,
          error: null,
          items,
          total,
          offset,
          limit,
        };
        state.appId = appId;
      })
      .addCase(fetchClientNotificationCenterReporting.rejected, (state, action) => {
        state.reporting.loading = false;
        state.reporting.error =
          action.payload ||
          action.error?.message || "Error";
      })
      .addCase(
        fetchClientNotificationCenterReportingCalendar.pending,
        (state, action) => {
          if (!action.meta.arg?.silent) {
            state.reporting_calendar.loading = true;
          }
          state.reporting_calendar.error = null;
        }
      )
      .addCase(
        fetchClientNotificationCenterReportingCalendar.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.reporting_calendar = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterReportingCalendar.rejected,
        (state, action) => {
          state.reporting_calendar.loading = false;
          state.reporting_calendar.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(fetchClientNotificationCenterMessages.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.messages.loading = true;
        }
        state.messages.error = null;
      })
      .addCase(fetchClientNotificationCenterMessages.fulfilled, (state, action) => {
        const { items, total, offset, limit, appId } = action.payload;
        state.messages = {
          loading: false,
          error: null,
          items,
          total,
          offset,
          limit,
        };
        state.appId = appId;
      })
      .addCase(fetchClientNotificationCenterMessages.rejected, (state, action) => {
        state.messages.loading = false;
        state.messages.error =
          action.payload ||
          action.error?.message || "Error";
      })
      .addCase(
        fetchClientNotificationCenterRegistrationTracker.pending,
        (state, action) => {
          if (!action.meta.arg?.silent) {
            state.registration_tracker.loading = true;
          }
          state.registration_tracker.error = null;
        }
      )
      .addCase(
        fetchClientNotificationCenterRegistrationTracker.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.registration_tracker = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterRegistrationTracker.rejected,
        (state, action) => {
          state.registration_tracker.loading = false;
          state.registration_tracker.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(
        fetchClientNotificationCenterThresholds.pending,
        (state, action) => {
          if (!action.meta.arg?.silent) {
            state.thresholds.loading = true;
          }
          state.thresholds.error = null;
        }
      )
      .addCase(
        fetchClientNotificationCenterThresholds.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.thresholds = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterThresholds.rejected,
        (state, action) => {
          state.thresholds.loading = false;
          state.thresholds.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(fetchClientNotificationCenterProduct.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.product.loading = true;
        }
        state.product.error = null;
      })
      .addCase(
        fetchClientNotificationCenterProduct.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.product = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterProduct.rejected,
        (state, action) => {
          state.product.loading = false;
          state.product.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(fetchClientNotificationCenterBilling.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.billing.loading = true;
        }
        state.billing.error = null;
      })
      .addCase(
        fetchClientNotificationCenterBilling.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.billing = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterBilling.rejected,
        (state, action) => {
          state.billing.loading = false;
          state.billing.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(
        fetchClientNotificationCenterSettings.pending,
        (state, action) => {
          if (!action.meta.arg?.silent) {
            state.settings.loading = true;
          }
          state.settings.error = null;
        }
      )
      .addCase(
        fetchClientNotificationCenterSettings.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.settings = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterSettings.rejected,
        (state, action) => {
          state.settings.loading = false;
          state.settings.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(
        fetchClientNotificationCenterInvoices.pending,
        (state, action) => {
          if (!action.meta.arg?.silent) {
            state.invoices.loading = true;
          }
          state.invoices.error = null;
        }
      )
      .addCase(
        fetchClientNotificationCenterInvoices.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.invoices = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterInvoices.rejected,
        (state, action) => {
          state.invoices.loading = false;
          state.invoices.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(
        fetchClientNotificationCenterTransactions.pending,
        (state, action) => {
          if (!action.meta.arg?.silent) {
            state.transactions.loading = true;
          }
          state.transactions.error = null;
        }
      )
      .addCase(
        fetchClientNotificationCenterTransactions.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.transactions = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterTransactions.rejected,
        (state, action) => {
          state.transactions.loading = false;
          state.transactions.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(
        fetchClientNotificationCenterConsumption.pending,
        (state, action) => {
          if (!action.meta.arg?.silent) {
            state.consumption.loading = true;
          }
          state.consumption.error = null;
        }
      )
      .addCase(
        fetchClientNotificationCenterConsumption.fulfilled,
        (state, action) => {
          const { items, total, offset, limit, appId } = action.payload;
          state.consumption = {
            loading: false,
            error: null,
            items,
            total,
            offset,
            limit,
          };
          state.appId = appId;
        }
      )
      .addCase(
        fetchClientNotificationCenterConsumption.rejected,
        (state, action) => {
          state.consumption.loading = false;
          state.consumption.error =
            action.payload ||
            action.error?.message || "Error";
        }
      )
      .addCase(markClientNotificationCenterAsSeen.rejected, (state, action) => {
        const tableId = resolveClientNotificationCenterTableId(
          action.payload?.tableId
        );
        state[tableId].error =
          action.payload?.message ||
          action.error?.message || "Error";
      });
  },
});

export const { clearClientNotificationCenterError } =
  clientNotificationCenterSlice.actions;

export default clientNotificationCenterSlice.reducer;
